/* eslint-disable max-depth */
/* eslint-disable max-statements */
import React, { useState, useEffect, forwardRef } from "react";
import PropTypes from "prop-types";
import _ from "lodash";


import { DoubleClickAd } from "../../legacy/components/results-doubleclick-ad";
import PhotoRowGridItem from "./PhotoRowGridItem/index";

import { useDimensions } from "../../hooks/useDimensions";

import { RowGridContainer, AdContainer, LingoAdContainer, LingoAdLink } from "./styles";

import { imageRatioToWidth } from "./helpers";
import lingoAd from '../../../img/ads/lingo-300x50-1.png';

// Constants
const MOBILE_WIDTH = 700;

// Maximum height an item can appear at
const maxItemHeight = 450;

// This is the heart of the layout algorithm
// There is probably some room for optimization here, but currently this does a few passes on the results
//
// Initial state
//      rowItems       - RowItem array with width/height set to base width/calculated height from image ratio
//      containerWidth - width of the container
//      fillRatio      - scale ratio needed to fill the container with all items at their base width
//
// 1st pass - fill the width of the container
//      Iterate through the row and temporarily scale all images to fill the row width
//      This is guaranteed to fill the width of the container, but is not guaranteed to
//      equalize the heights of the items
//
// 2nd pass - equalize the heights of the items
//      This finds the minimum of the resized heights, and iterates over the items again
//      in order to calculate a new fill ratio that will resize all the other items in the row
//      to match the minimum height
//
// 3rd pass - generate the react components
//      Mutate the itemsDisplay argument and instantiate the actual React components for the items
//      If an equalize heights pass was performed, this also applies the new fill ratio that is now
//      guaranteed to fill the row width with items of equal heights.

const renderItemsInRow = ({
    rowItems,
    itemsDisplay,
    fillRatio,
    containerWidth,
    gridItemComponent,
    rowNumber,
    subscribed,
    isMobile,
    useRouterLink
}) => {
    // This algorithm will assign elements fractional widths,
    // some browsers round these up (causing wrapping that breaks the layout),
    // while others round down.
    //
    // Targeting a 1px smaller container accomodates both, with the downside
    // that in certain cases there might be 1px of space on the right side of a row. However,
    // since there is a scrollbar on that side, this hopefully isn't too noticable.
    const finalContainerWidth = containerWidth - 1;
    let filledRow = [];

    rowItems.forEach(el => {
        let { width, height, index, photo } = el;
        let newWidth = width * fillRatio;
        let fillNewHeight = newWidth / photo.ratio;
        let newHeight = Math.min(fillNewHeight, maxItemHeight);
        filledRow.push({
            width,
            height,
            newWidth,
            newHeight,
            photo,
            index
        });
    });

    if (filledRow.length) {
        let equalizeHeights = filledRow.length > 1;
        let fillUpRatio = 1;

        if (equalizeHeights) {
            let min = _.minBy(filledRow, "newHeight");

            if (!min) {
                min = filledRow[0];
            }

            let minHeight = Math.min(min.newHeight, maxItemHeight);
            let rowWidth = 0;

            filledRow.forEach(item => {
                item.newHeight = minHeight;
                item.newWidth = minHeight * item.photo.ratio;
                rowWidth += item.newWidth;
            });

            fillUpRatio = rowWidth / finalContainerWidth;
        }

        if (!subscribed && (rowNumber === 3 || (rowNumber >= 3 && ((rowNumber - 3) % 7) === 0))) {
            if (window.innerWidth < 768) {
                // TODO: Replace with Square Space Mobile Ads
                itemsDisplay.push(
                    <LingoAdContainer key={`adRow-${rowNumber}`}>
                        <LingoAdLink href="https://www.lingoapp.com/?utm_source=noun_project">
                            <img src={lingoAd} width="300" height="50" alt="Lingo"/>
                        </LingoAdLink>
                    </LingoAdContainer>
                );
            } else {
                itemsDisplay.push(
                    <AdContainer data-testid="ad-container" key={`adRow-${rowNumber}`} >
                         <DoubleClickAd style={{ }}/>
                    </AdContainer>
                );
            }

        }

        filledRow.forEach(item => {
            let { newHeight, newWidth, photo } = item;

            if (equalizeHeights) {
                newHeight /= fillUpRatio;
                newHeight = Math.min(newHeight, maxItemHeight);
                newWidth = newHeight * item.photo.ratio;
            }

            itemsDisplay.push(<PhotoRowGridItem
                width={isMobile ? 'auto' : newWidth}
                height={isMobile ? 'auto' : newHeight}
                key={photo.id}
                photo={photo}
                gridItemComponent={gridItemComponent}
                useRouterLink={useRouterLink}
            />);
        });
    }
};

// eslint-disable-next-line max-statements
const PhotoResultRowGridBody = ({
    forwardedRef,
    results,
    gridItemComponent,
    subscribed,
    useRouterLink,
    maxRows
}) => {
    let isReady = true;

    // forwardedRef allows for a ref to be passed down from a parent
    // component, if one is not passed a local ref will be used.
    let [_ref, dimensions] = useDimensions({
        ref: forwardedRef
    });

    if (!forwardedRef) {
        isReady = false;
        forwardedRef = _ref;
    }
    if (!dimensions) {
        isReady = false;
    }

    let [ready, setReady] = useState(isReady);

    // Wait for a ref to be present, and dimensions computed
    useEffect(() => {
        if (!ready && dimensions) {
            setReady(true);
        }
    }, [forwardedRef, ready, dimensions]);

    let { items } = results;
    let previousRow = [];
    let rowWidth = 0;
    let containerWidth = dimensions && dimensions.width;
    let itemsDisplay = [];
    let rowNumber = 1;
    const isMobile = window.innerWidth <= MOBILE_WIDTH;

    for (let i = 0; i < items.length; i += 1) {
        if (containerWidth) {
            const photo = items[i];

            let { ratio } = photo;
            let { width } = imageRatioToWidth({ ratio });
            let thisItem = {
                width,
                height: width / ratio,
                index: i,
                photo
            };

            // Handle all mobile views
            if (isMobile) {
                renderItemsInRow({
                    rowItems: [thisItem],
                    itemsDisplay,
                    fillRatio: 1,
                    containerWidth,
                    gridItemComponent,
                    useRouterLink,
                    rowNumber,
                    isMobile,
                    subscribed
                });
                rowNumber += 1;
            }

            // Handle all non-mobile views
            if (!isMobile) {
                if ((rowWidth + width) < containerWidth) {
                        rowWidth += width;
                        previousRow.push(thisItem);
                } else {
                    if (rowWidth < containerWidth) {

                        let fillRatio = 1 / (rowWidth / containerWidth);

                        renderItemsInRow({
                            rowItems: previousRow,
                            itemsDisplay,
                            fillRatio,
                            containerWidth,
                            gridItemComponent,
                            rowNumber,
                            subscribed,
                            useRouterLink
                        });
                        rowNumber += 1;

                    }
                    previousRow = [thisItem];
                    rowWidth = width;
                }
            }

            // Stop rendering rows after we've rendered `maxRows`
            if (maxRows && rowNumber > maxRows) {
                break;
            }
        }
    }

    // Don't render the last row if maxRows is defined and we've already rendered
    // all the rows we need.
    const renderLastRow = !(maxRows && rowNumber > maxRows);
    if (
        itemsDisplay.length !== items.length &&
        previousRow.length &&
        renderLastRow
    ) {
        // Trying to fill the entire row with a single image will result in a very large image
        // also it will likely run up against maxItemHeight constraint and therefore not be
        // able to display at its native ratio and take up the full width of the page.
        let isSingle = previousRow.length === 1;

        let fillRatio = isSingle ? 1 : 1 / (rowWidth / containerWidth);

        renderItemsInRow({
            rowItems: previousRow,
            itemsDisplay,
            fillRatio,
            containerWidth,
            gridItemComponent,
            useRouterLink
        });
    }

    return (
        <RowGridContainer firstRender={!ready} ref={forwardedRef}>
            {itemsDisplay}
        </RowGridContainer>
    );
};

PhotoResultRowGridBody.propTypes = {
    results: PropTypes.object.isRequired,
    subscribed: PropTypes.bool,
    forwardedRef: PropTypes.object,
    gridItemComponent: PropTypes.any,
    useRouterLink: PropTypes.bool,
    maxRows: PropTypes.number
};

export const PhotoResultRowGrid = forwardRef((props, ref) => (
    <PhotoResultRowGridBody {...props} forwardedRef={ref} />
));

PhotoResultRowGrid.displayName = 'PhotoResultRowGrid';
